import ActionDialog from "@dashboard/components/ActionDialog";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DetailPageLayout } from "@dashboard/components/Layouts";
import NotFoundPage from "@dashboard/components/NotFoundPage";
import {
  useWsmFitmentAxisDeleteMutation,
  useWsmFitmentAxisUpdateMutation,
  useWsmFitmentAxisValueBulkCreateMutation,
  useWsmFitmentAxisValueDeleteMutation,
  useWsmFitmentAxisValuesQuery,
  useWsmFitmentAxisValueUpdateMutation,
  WsmErrorFragment,
} from "@dashboard/graphql";
import useDebounce from "@dashboard/hooks/useDebounce";
import useNavigator from "@dashboard/hooks/useNavigator";
import useNotifier from "@dashboard/hooks/useNotifier";
import { commonMessages } from "@dashboard/intl";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import { Skeleton, Text } from "@saleor/macaw-ui-next";
import React, { useRef, useState } from "react";
import { useIntl } from "react-intl";

import { removeFromWsmFitmentList } from "../cache";
import { WsmFitmentLoadError } from "../components/WsmFitmentLoadError";
import WsmFitmentNamedDetailsPage from "../components/WsmFitmentNamedDetailsPage";
import WsmFitmentValuesCard from "../components/WsmFitmentValuesCard";
import { getWsmFitmentErrorMessage } from "../errors";
import { useWsmFitmentAttempt } from "../hooks/useWsmFitmentAttempt";
import {
  useWsmFitmentEmptyPageFallback,
  useWsmFitmentPagedList,
} from "../hooks/useWsmFitmentPagedValues";
import { useWsmFitmentAxes } from "../hooks/useWsmFitmentVocabulary";
import { axisMessages, sectionMessages, valueMessages } from "../messages";
import { wsmFitmentAxisListPath } from "../urls";
import { getWriteState, toWriteResult } from "../utils";

export const WsmFitmentAxisDetailsView = ({ id }: { id: string }) => {
  const intl = useIntl();
  const navigate = useNavigator();
  const notify = useNotifier();
  const [deleteOpen, setDeleteOpen] = useState(false);
  // Shown on the delete dialog: this call's result only, cleared on open.
  const [deleteErrors, setDeleteErrors] = useState<WsmErrorFragment[]>([]);
  const deleteAttempt = useWsmFitmentAttempt();
  const { axes, loading, error: listError, retry } = useWsmFitmentAxes();
  const { variables, bind, resetPage } = useWsmFitmentPagedList();
  // The box shows every keystroke; the query takes the text once typing pauses.
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const applySearch = useDebounce((value: string) => {
    resetPage();
    setSearch(value.trim());
  }, 500);
  const { data: valuesData, loading: valuesLoading } = useWsmFitmentAxisValuesQuery({
    variables: { axis: id, search: search || null, ...variables },
  });
  const valueRows = valuesLoading
    ? undefined
    : (mapEdgesToItems(valuesData?.wsmFitmentAxisValues) ?? []);

  // Deleting the last row of the last page must not strand the table there.
  useWsmFitmentEmptyPageFallback(variables, valueRows, resetPage);

  // Payload errors render on the form and the dialogs; a toast would say it twice.
  const [updateAxis, updateAxisOpts] = useWsmFitmentAxisUpdateMutation({
    disableNestedErrorHandling: true,
    onCompleted: result => {
      if (!result.wsmFitmentAxisUpdate?.errors.length) {
        notify({ status: "success", text: intl.formatMessage(commonMessages.savedChanges) });
      }
    },
  });
  const [deleteAxis] = useWsmFitmentAxisDeleteMutation({
    disableNestedErrorHandling: true,
    // The row leaves the cached list here, so the list is right the moment it
    // mounts; the awaited refetch then lands while this screen still holds the
    // query, instead of being cancelled by the navigation.
    update: (cache, { data }) => {
      if (data?.wsmFitmentAxisDelete && !data.wsmFitmentAxisDelete.errors.length) {
        removeFromWsmFitmentList(cache, "wsmFitmentAxes", { __typename: "WsmFitmentAxis", id });
      }
    },
    refetchQueries: ["WsmFitmentAxes"],
    awaitRefetchQueries: true,
    onCompleted: result => {
      if (result.wsmFitmentAxisDelete && !result.wsmFitmentAxisDelete.errors.length) {
        notify({ status: "success", text: intl.formatMessage(commonMessages.savedChanges) });
        navigate(wsmFitmentAxisListPath);
      }
    },
  });
  const [addValues, addValuesOpts] = useWsmFitmentAxisValueBulkCreateMutation({
    disableNestedErrorHandling: true,
    refetchQueries: ["WsmFitmentAxisValues"],
  });
  const [renameValue, renameValueOpts] = useWsmFitmentAxisValueUpdateMutation({
    disableNestedErrorHandling: true,
  });
  const [deleteValue, deleteValueOpts] = useWsmFitmentAxisValueDeleteMutation({
    disableNestedErrorHandling: true,
    refetchQueries: ["WsmFitmentAxisValues"],
  });
  const found = axes.find(candidate => candidate.id === id);
  // This screen's own delete takes the row out of the cache before it navigates
  // away; until then it keeps showing the last copy rather than "Not found".
  const lastSeen = useRef(found);

  if (found) {
    lastSeen.current = found;
  }

  const deleting = deleteAttempt.state === "loading" || deleteAttempt.state === "success";
  const axis = found ?? (deleting ? lastSeen.current : undefined);

  // A list that failed to load says so, rather than calling the row missing.
  if ((loading || listError) && !axis) {
    return (
      <DetailPageLayout>
        <TopNav href={wsmFitmentAxisListPath} title={intl.formatMessage(sectionMessages.axes)} />
        <DetailPageLayout.Content>
          {listError ? <WsmFitmentLoadError onRetry={retry} /> : <Skeleton />}
        </DetailPageLayout.Content>
      </DetailPageLayout>
    );
  }

  if (!axis) {
    return <NotFoundPage backHref={wsmFitmentAxisListPath} />;
  }

  const updatePayload = updateAxisOpts.data?.wsmFitmentAxisUpdate;
  const errors = updatePayload?.errors ?? [];
  const valuesBusy = addValuesOpts.loading || renameValueOpts.loading || deleteValueOpts.loading;

  return (
    <>
      <WsmFitmentNamedDetailsPage
        key={axis.id}
        title={axis.name}
        backHref={wsmFitmentAxisListPath}
        withValueKind
        initialData={{
          name: axis.name,
          displayLabel: axis.displayLabel,
          valueKind: axis.valueKind,
        }}
        errors={errors}
        disabled={updateAxisOpts.loading}
        saveButtonBarState={getWriteState(updateAxisOpts, updatePayload)}
        onSubmit={data =>
          updateAxis({
            variables: {
              id: axis.id,
              input: {
                name: data.name.trim(),
                displayLabel: data.displayLabel.trim(),
                valueKind: data.valueKind,
              },
            },
          })
        }
        onDelete={() => {
          setDeleteErrors([]);
          deleteAttempt.reset();
          setDeleteOpen(true);
        }}
      >
        <WsmFitmentValuesCard
          title={intl.formatMessage(axisMessages.valuesTitle)}
          values={valueRows}
          totalCount={valuesData?.wsmFitmentAxisValues?.totalCount}
          paging={bind(valuesData?.wsmFitmentAxisValues?.pageInfo)}
          disabled={valuesBusy}
          search={{
            value: searchText,
            onChange: value => {
              setSearchText(value);
              applySearch(value);
            },
          }}
          deleteMessage={valueMessages.deleteContent}
          onAdd={async values => {
            const result = await addValues({ variables: { axis: axis.id, values } });
            const payload = result.data?.wsmFitmentAxisValueBulkCreate;

            if (payload && !payload.errors.length) {
              notify({
                status: "success",
                text: intl.formatMessage(valueMessages.addCount, { count: payload.count }),
              });
            }

            return toWriteResult(payload);
          }}
          onRename={async (valueId, value) => {
            const result = await renameValue({ variables: { id: valueId, input: { value } } });

            return toWriteResult(result.data?.wsmFitmentAxisValueUpdate);
          }}
          onDelete={async valueId => {
            const result = await deleteValue({ variables: { id: valueId } });

            return toWriteResult(result.data?.wsmFitmentAxisValueDelete);
          }}
        />
      </WsmFitmentNamedDetailsPage>
      <ActionDialog
        open={deleteOpen}
        variant="delete"
        title={intl.formatMessage(axisMessages.deleteTitle)}
        confirmButtonState={deleteAttempt.state}
        onClose={() => setDeleteOpen(false)}
        onConfirm={async () => {
          const result = await deleteAttempt.run(async () =>
            toWriteResult(
              (await deleteAxis({ variables: { id: axis.id } })).data?.wsmFitmentAxisDelete,
            ),
          );

          setDeleteErrors(result.errors);
        }}
      >
        <Text display="block">
          {intl.formatMessage(axisMessages.deleteContent, { name: axis.name })}
        </Text>
        {deleteErrors[0] && (
          <Text
            display="block"
            color="critical1"
            marginTop={2}
            data-test-id="wsm-fitment-axis-delete-error"
          >
            {getWsmFitmentErrorMessage(deleteErrors[0], intl)}
          </Text>
        )}
      </ActionDialog>
    </>
  );
};

export default WsmFitmentAxisDetailsView;
